// pricingSidepanel.tsx
import './pricingSidepanel.css';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronsRight as ChevronsRightIcon, ArrowRight } from 'lucide-react';
import PdfViewButton from '../../../common/pdf_view_button/pdfViewButton';
import { useMonthNavigation } from '../../../../contexts/monthNavigationContext';
import { useDataManager } from '../../../../contexts/dataManager';
import { convertMonth, getUniqueDropdownOptions } from '../../../../helper_functions';
import TableTextInput from '../../../common/table_text_input/tableTextInput';
import Checkbox from '../../../common/checkbox/checkbox';
import SQLTable from '../../tableMain';
import TableInput from '../../../common/table_input/tableInput';
import { getInputConfig } from '../../../../pages/AccTables/accTablesConfig';
import { useApiData } from '../../../../contexts/apiService';

interface PricingSidePanel {
    isOpen: boolean;
    onEditingStateChange?: (isEditing: boolean) => void;
    panelData?: {
        rowData: any;
        colName: string;
        tableName: string;
    };
}

const PricingSidePanel: React.FC<PricingSidePanel> = ({
    isOpen,
    onEditingStateChange,
    panelData,
}) => {
    const { apiData } = useApiData();
    const [isAnyItemEditing, setIsAnyItemEditing] = useState(false);
    
    // Callback to receive editing state from SequenceComponent
    const handleEditingStateChange = (isEditing: boolean) => {
        setIsAnyItemEditing(isEditing);
        onEditingStateChange?.(isEditing);
    };

    const producerOptions = getUniqueDropdownOptions({
        tableData: apiData?.producer_definition,
        valueColumn: 'id',
        labelColumn: 'producer_name',
    });
    
    if (!isOpen) return null;

    return (
        <div className="pricingSidePanel-mainContainer">
            
            <div className="pricingSidePanel-header">
                <div>{panelData?.rowData.carrier_name}</div>
            </div>

            <div className="pricingSidePanel-titleContainer">
                <div>{convertMonth(panelData?.rowData.month)} {panelData?.rowData.year}</div>
            </div>

            <div className="pricingSidePanel-pricingContainer">
                <TableTextInput
                    tableName="carrier_pricing"
                    rowId={panelData?.rowData?.id}
                    columnName="rate_hourly"
                    value={panelData?.rowData?.rate_hourly}
                    placeholder="Enter rate"
                    label="Vac Truck Hourly Rate"
                    width={160}
                    height={28}
                    validate={(val) => isNaN(Number(val)) ? 'Must be a number' : null}
                />
                <TableTextInput
                    tableName="carrier_pricing"
                    rowId={panelData?.rowData?.id}
                    columnName="fw_bbl_rate"
                    value={panelData?.rowData?.fw_bbl_rate}
                    placeholder="Enter rate"
                    label="Fresh Water Bbl Rate"
                    width={160}
                    height={28}
                    validate={(val) => isNaN(Number(val)) ? 'Must be a number' : null}
                />
                <TableTextInput
                    tableName="carrier_pricing"
                    rowId={panelData?.rowData?.id}
                    columnName="disposal_rate"
                    value={panelData?.rowData?.disposal_rate}
                    placeholder="Enter rate"
                    label="Disposal Rate"
                    width={160}
                    height={28}
                    validate={(val) => isNaN(Number(val)) ? 'Must be a number' : null}
                />
                <div className="pricingSidePanel-pricingContainerTable">
                    <Checkbox
                        label="Producer specific rates"
                        checked={panelData?.rowData?.has_prod_rate || false}
                        editConfig={{
                            tableName: 'carrier_pricing',
                            rowId: panelData?.rowData?.id,
                            columnName: 'has_prod_rate',
                        }}
                    />
                    {panelData?.rowData?.has_prod_rate ? (
                        <div>
                            <TableInput
                                tableName={'carrier_pricing_producer'}
                                parentId={panelData?.rowData?.id}
                                inputColumnConfigs={[
                                    {
                                        colName: 'producer_id',
                                        colType: 'number',
                                        inputType: 'dropdown',
                                        dropdownOptions: producerOptions,
                                        required: true,
                                        placeholder: 'Producer',
                                    },
                                ]}
                            />

                            <SQLTable 
                                tableName="carrier_pricing_producer"
                                tableData={apiData?.carrier_pricing_producer ?? []}
                                idColumn='id'
                                columnConfigs={[
                                    {
                                        colName: 'producer_name',
                                        colHeader: 'Producer',
                                        colType: 'text',
                                        editable: false,
                                    },
                                    {
                                        colName: 'rate_hourly',
                                        colHeader: 'Hourly Rate',
                                        colType: 'number',
                                        inputType: 'input',
                                        editable: true,
                                    },
                                    {
                                        colName: 'fw_bbl_rate',
                                        colHeader: 'FW Bbl Rate',
                                        colType: 'number',
                                        inputType: 'input',
                                        editable: true,
                                    },
                                ]}
                                showTable={true}
                                deletableRows={true}
                                selectableRows={true}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default PricingSidePanel;
